import PersonAddIcon from "@mui/icons-material/PersonAdd";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid2";
import Modal from "@mui/material/Modal";
import Typography from "@mui/material/Typography";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";
import { BaseUserSchema, CoworkerSchema } from "../../../api/schemas/responses/users";
import UserSelector from "../../../components/selectors/UserSelector";
import UserPreview from "../../../components/UsersPreview/UserPreview";
import OutcomeInvite from "../../invites/components/OutcomeInvite";
import projectStore from "../store";
import { InviteInterface, ProjectInterface, UpdateProjectInterface } from "../interfaces";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@mui/material";
import TextField from "@mui/material/TextField";
import * as React from "react";
import ProjectsApi from "../api";
import { useApiCall } from "../../../hooks/useApiCall";
import i18nStore from "../../../i18n/store";

const ProjectMainTab = observer(() => {
    return (
        <>
            <Grid
                container
                spacing={1}
                sx={{
                    justifyContent: "center",
                }}
            >
                <Grid size={{ xs: 12, md: 8, xl: 6 }}>

                    <GeneralSection />
                    <PeopleSection />
                    <DangerSection />
                </Grid>
            </Grid>
        </>
    );
});

export default ProjectMainTab;


interface BaseSectionProps {
    title: string;
    children?: any;
}
function BaseSection(props: BaseSectionProps) {
    return (
        <Box sx={{ mt: 3 }}>
            <Typography variant="h6">
                {props.title}
            </Typography>
            <Divider sx={{ mb: 2 }}/>
            {props.children || null}
        </Box>
    );
}

const GeneralSection = observer(() => {
    const translations = i18nStore.getTranslations;
    const params = useParams();
    const currentProjectId: number = params.projectId ? +params.projectId : 0;
    const getProjectApi = useApiCall(() => ProjectsApi.get_by_id(currentProjectId), null)
    const [title, setTitle] = React.useState<string>("");
    const [description, setDescription] = React.useState<string>("");

    useEffect(() => {
        getProjectApi.apiCaller()?.then((data: ProjectInterface | any) => {
            setTitle(data.title);
            setDescription(data.description);
        });
    }, []);

    const updateProjectApi = useApiCall(
        () => ProjectsApi.update(currentProjectId, {
            title: title,
            description: description,
        } as UpdateProjectInterface),
        null,
    )

    const onSaveBtnClickHandler = () => {
        updateProjectApi.apiCaller();
    };

    return (
        <BaseSection title= {translations.generalSet} >
            <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "flex-start", alignItems: "flex-start" }}>
                <TextField
                    label={translations.name}
                    variant="outlined"
                    size="small"
                    value={title}
                    onChange={e => setTitle(e.target.value)}
                    sx={{ mb: 2, minWidth: 300 }}
                    slotProps={{
                        inputLabel: {
                            shrink: true,
                        },
                    }}
                />

                <TextField
                    label={translations.description}
                    variant="outlined"
                    size="small"
                    value={description}
                    onChange={e => setDescription(e.target.value)}
                    sx={{ mb: 2, minWidth: 300 }}
                    slotProps={{
                        inputLabel: {
                            shrink: true,
                        },
                    }}
                />

                <Button
                    variant="contained"
                    color="success"
                    sx={{ minWidth: "100px"}}
                    onClick={onSaveBtnClickHandler}
                    loading={updateProjectApi.loading}
                >
                    {translations.save}
                </Button>
            </Box>
        </BaseSection>
    );
});

const PeopleSection = observer(() => {
    const [openInviteModal, setOpenInviteModal] = useState(false);
    const [userToInvite, setUserToInvite] = useState<BaseUserSchema | null>(null);

    const params = useParams();
    const translations = i18nStore.getTranslations;
    const currentProjectId = params.projectId ? +params.projectId : 0;
    const currentProject: ProjectInterface | undefined = projectStore.getProjectById(currentProjectId);

    const coworkersApi = useApiCall<CoworkerSchema[]>(
        () => ProjectsApi.getProjectCoworkers(currentProjectId),
        [],
    );

    const invitesApi = useApiCall<InviteInterface[]>(
        () => ProjectsApi.getInvites(currentProjectId).then((res) => {
            setOpenInviteModal(false);
            return res;
        }),
        [],
    );

    useEffect(() => {
        if (currentProjectId) {
            coworkersApi.apiCaller();
            invitesApi.apiCaller();
        }
    }, [currentProjectId]);

    const inviteUser = () => {
        if (userToInvite && currentProjectId) {
            ProjectsApi.createInvite(userToInvite.id, currentProjectId)
                .then(() => {
                    invitesApi.apiCaller();
                });
        }
    };

    if (!currentProject) return null;
    const style = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 400,
        bgcolor: 'background.paper',
        border: '2px solid #000',
        boxShadow: 24,
        p: 4,
    };
    return (
        <BaseSection title={translations.people}>
                <Modal
                    open={openInviteModal}
                    onClose={() => setOpenInviteModal(false)}
                >
                    <Box sx={style}>
                        <Typography variant="h6" component="h2" sx={{ mb: 2 }}>
                            {translations._invites.inviteNewUser}
                        </Typography>
                        <UserSelector user={userToInvite} setUser={setUserToInvite} />
                        <Button
                            variant="contained"
                            sx={{ my: 1 }}
                            fullWidth
                            disabled={!userToInvite}
                            onClick={inviteUser}
                        >
                            {translations._invites.invite}
                        </Button>
                    </Box>
                </Modal>
                <Button
                    variant="contained"
                    startIcon={<PersonAddIcon />}
                    sx={{ mb: 2 }}
                    onClick={() => setOpenInviteModal(true)}
                >
                    {translations._invites.invite}
                </Button>

                <Box>
                    {
                        invitesApi.data.length > 0 &&
                        <Typography variant="body2" sx={{ mt: 3 }}>
                            {translations.sentInvites}:
                        </Typography>
                    }
                    {
                        invitesApi.data.map((invite) => (
                            <OutcomeInvite invite={invite} onRevokeInvite={invitesApi.apiCaller} />
                        ))
                    }
                </Box>

                {
                    coworkersApi.data.length !== 0 && <Box>
                        <Typography>{translations.users + ":"}</Typography>
                        {
                            coworkersApi.data.map((user: CoworkerSchema) => (
                                <UserPreview key={user.id} user={user} userPost={user.role || ""} />
                            ))
                        }
                    </Box>
                }
        </BaseSection>
    );
});

const DangerSection = observer(() => {
    const translations = i18nStore.getTranslations;
    return (
        <BaseSection title= {translations.dangerZone}>
            <DeleteProjectButton />
        </BaseSection>
    );
});

const DeleteProjectButton = () => {
    const translations = i18nStore.getTranslations;
    const navigate = useNavigate();
    const params = useParams();
    const currentProjectId = params.projectId ? +params.projectId : 0;
    const deleteProjectApi = useApiCall(
        () => ProjectsApi.delete(currentProjectId)
            .then(() => {
                projectStore.prefetchData();
                navigate("/projects");
            })
            .catch(() => {}),
        null,
    )
    return (
        <Button
            variant="contained"
            color="error"
            sx={{ minWidth: "100px"}}
            onClick={deleteProjectApi.apiCaller}
        >
            {translations.delete}
        </Button>
    );
}
